import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';

import { ApiUrlService } from './api-url.service';
import { SolicitudDetalleResponse, SolicitudesListadoResponse, CrearNuevaSolicitud } from '../models/solicitudes';

@Injectable({
  providedIn: 'root'
})
export class SolicitudesService {
  private readonly pendientesRefreshSubject = new Subject<void>();
  readonly pendientesRefresh$ = this.pendientesRefreshSubject.asObservable();

  constructor(private apiUrl: ApiUrlService, private http: HttpClient) {
  }

  public notificarPendientesRefresh(): void {
    this.pendientesRefreshSubject.next();
  }
  //METODOS DE LLAMADA
  public obtenerListadoSolicitudes(): Observable<SolicitudesListadoResponse> {
    const url = `${this.apiUrl.listadoSolicitudes}`;
    return this.http.get<SolicitudesListadoResponse>(url);
  }

  public obtenerDetalleSolicitud(idSolicitud: number): Observable<SolicitudDetalleResponse> {
    const url = `${this.apiUrl.detalleSolicitud}`;
    const params = new HttpParams().set('id_solicitud', String(idSolicitud));
    return this.http.get<SolicitudDetalleResponse>(url, { params });
  }

  public obtenerImagenSolicitud(titulo: string, cacheBuster?: number): Observable<Blob> {
    const url = `${this.apiUrl.solicitudImage}`;
    let params = new HttpParams().set('titulo', titulo);
    if (cacheBuster) {
      params = params.set('t', String(cacheBuster));
    }
    return this.http.get(url, { params, responseType: 'blob' });
  }

  public actualizarDetalleSolicitud(
    idSolicitud: number,
    solicitud: Partial<SolicitudDetalleResponse['solicitud']> & {
      id_usuario?: string | null;
      titulo?: string | null;
      estado?: string | null;
      tipo_solicitud?: string | null;
      envases?: number | string | null;
      cantidad?: number | string | null;
      prescriptor?: string | null;
      observaciones?: string | null;
      observaciones_admin?: string | null;
      email_externo?: string | null;
      createdAt?: Date | string | null;
      updatedAt?: Date | string | null;
      fecha_creacion?: Date | string | null;
      fecha_actualizacion?: Date | string | null;
      adjunto_extension?: string | null;
      adjunto_tipo_mime?: string | null;
      adjunto_url?: string | null;
      creado_por?: string | null;
    }
  ): Observable<SolicitudDetalleResponse> {
    const url = `${this.apiUrl.actualizarSolicitud}`;
    const params = new HttpParams().set('id_solicitud', String(idSolicitud));
    const tipoSolicitudBack = this.normalizarTipoSolicitudBackend(solicitud?.tipo_solicitud ?? '');
    const payload: Record<string, any> = {
      id_solicitud: idSolicitud,
      id_usuario: solicitud?.id_usuario ?? null,
      titulo: solicitud?.titulo ?? '',
      estado: solicitud?.estado ?? '',
      tipo_solicitud: tipoSolicitudBack,
      creado_por: solicitud?.creado_por ?? null,
      envases: solicitud?.envases ?? null,
      cantidad: solicitud?.cantidad ?? null,
      prescriptor: solicitud?.prescriptor ?? null,
      nombre_paciente: (solicitud as any)?.nombre_paciente ?? (solicitud as any)?.nombrePaciente ?? null,
      observaciones: solicitud?.observaciones ?? null,
      observaciones_admin: solicitud?.observaciones_admin ?? null,
      email_externo: solicitud?.email_externo ?? null,
      adjunto_extension: solicitud?.adjunto_extension ?? null,
      adjunto_tipo_mime: solicitud?.adjunto_tipo_mime ?? null,
      adjunto_url: solicitud?.adjunto_url ?? null,
      fecha_creacion: solicitud?.fecha_creacion ?? solicitud?.createdAt ?? null,
      fecha_actualizacion: solicitud?.fecha_actualizacion ?? solicitud?.updatedAt ?? null
    };
    return this.http.put<SolicitudDetalleResponse>(url, payload, { params });
  }


  public actualizarEstadoSolicitud(
    idSolicitud: number,
    estado: string,
    comentario?: string,
    idUsuario?: string | null
  ): Observable<SolicitudDetalleResponse> {
    const url = `${this.apiUrl.actualizarSolicitudEstado}`;
    const params = new HttpParams().set('id_solicitud', String(idSolicitud));
    const payload: Record<string, any> = { estado };
    if (comentario) {
      payload['comentario'] = comentario;
    }
    if (idUsuario) {
      payload['id_usuario'] = idUsuario;
    }
    return this.http.put<SolicitudDetalleResponse>(url, payload, { params });
  }

  public obtenerHistorialSolicitud(idSolicitud: number): Observable<any> {
    const url = `${this.apiUrl.obtenerHistorialSolicitud}`;
    const params = new HttpParams().set('id_solicitud', String(idSolicitud));
    return this.http.get<any>(url, { params });
  }
  
  public crearSolicitud(payload: CrearNuevaSolicitud | FormData): Observable<SolicitudDetalleResponse> {
    const url = `${this.apiUrl.crearSolicitud}`;
    if (payload instanceof FormData) {
      const tipoSolicitud = payload.get('tipoSolicitud') ?? payload.get('tipo_solicitud');
      if (tipoSolicitud !== null) {
        const tipoSolicitudBack = this.normalizarTipoSolicitudBackend(tipoSolicitud);
        if (payload.has('tipoSolicitud')) {
          payload.set('tipoSolicitud', tipoSolicitudBack);
        }
        if (payload.has('tipo_solicitud')) {
          payload.set('tipo_solicitud', tipoSolicitudBack);
        }
      }
      console.log('Payload crearSolicitud:', payload);
      return this.http.post<SolicitudDetalleResponse>(url, payload);
    }
    const tipoSolicitudRaw = payload?.tipoSolicitud ?? payload?.tipo_solicitud ?? '';
    const tipoSolicitudBack = this.normalizarTipoSolicitudBackend(tipoSolicitudRaw);
    const payloadNormalizado: CrearNuevaSolicitud = {
      ...payload,
      tipoSolicitud: tipoSolicitudBack,
      tipo_solicitud: tipoSolicitudBack
    };
    console.log('Payload crearSolicitud:', payloadNormalizado);
    return this.http.post<SolicitudDetalleResponse>(url, payloadNormalizado);
  }

  public subirImagenSolicitud(titulo: string, imagen: File): Observable<{ message?: string; titulo?: string }> {
    const url = `${this.apiUrl.uploadImagenSolicitud}`;
    const payload = new FormData();
    payload.append('titulo', titulo);
    payload.append('imagen', imagen, imagen.name);
    console.log('Payload subirImagenSolicitud:', {
      titulo,
      imagenNombre: imagen?.name,
      imagenTipo: imagen?.type,
      imagenSize: imagen?.size
    });
    return this.http.post<{ message?: string; titulo?: string }>(url, payload);
  }

  private normalizarTipoSolicitudBackend(raw: unknown): string {
    const tipo = (raw ?? '').toString().trim().toLowerCase();
    if (!tipo) return '';
    if (tipo === 'solicitud interna') return 'SOLICITUD INTERNA';
    if (tipo === 'solicitud externa') return 'SOLICITUD EXTERNA';
    if (tipo === 'cliente directo') return 'CLIENTE DIRECTO';
    return '';
  }

}
